import React from 'react';
// Shared with the PRO's Quote Charges panel, which displays the same stored rate.
import { formatQuoteRate } from '../../../utils/money';
import { TruckIcon, ChatBubbleLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput, { type SelectOption } from '../../pro/components/orderform/SelectInput';
import FlagInput, { type FlagValue } from '../../pro/components/orderform/FlagInput';
import Icon from '../../pro/components/orderform/Icon';
import Card from '../components/Card';
import FieldValue from '../components/FieldValue';
import AutoCalcRow from '../components/AutoCalcRow';
import TariffRatesCard from '../components/TariffRatesCard';
import type { DirectFields, TariffRateRow, StopoffRow, LocationLookupRow } from '../fixtures';
import type { ChargeCatalogRow, StopoffFieldsPayload } from '../../../services/quote.service';
import StopoffsEditor from '../components/StopoffsEditor';
import LocationTypeAhead from '../components/LocationTypeAhead';
import useQuoteDeliveryGeo from '../components/useQuoteDeliveryGeo';
import useQuoteFsc, { fscRateUnit } from '../components/useQuoteFsc';
import { MISSING_TARIFF_LABEL, DELETED_TARIFF_LABEL, isMissingCustomerTariff } from '../../../utils/fscTariff';
import type { FscTariff } from '../../../services/quote.service';
import FscTariffLookupModal from '../components/FscTariffLookupModal';
import ShowMilesModal from '../components/ShowMilesModal';
import DrayRateHint from '../components/DrayRateHint';
import AlertModal from '../../../components/ui/AlertModal';
import { MOVE_TYPES, SIZES, EQUIP_TYPES, RATE_ZONES, DELIVERY_ZONES, FSC_TYPES, CHASSIS_FEE_TYPES, FUEL_CALC_TYPES, RATE_APPLIES, withCurrent } from '../quoteOptions';
import { chassisDaysReadOnly, triaxleReadOnly, swingReadOnly } from '../quoteFieldRules';
import { quoteOverweight } from '../quoteOverweight';
import { quoteWeightCascade, type QuoteWeightField } from '../quoteWeights';
import './directTab.css';

const LBL = 110;

interface Props {
  data: DirectFields;
  tariffRates?: TariffRateRow[];
  /**
   * Optional change callback. When provided, the editable inputs become
   * controlled-editable and emit `(field, value)` where `field` is the
   * camelCase `DirectFields` key (matching `value=` source) and `value` is
   * the new string. When ABSENT, every input renders read-only display
   * exactly as before — existing read-only callers are unaffected.
   *
   * Note: flag values ('Y'|'N'|'P') are emitted as strings via this same
   * callback; Task 7 maps the camelCase keys to backend snake_case columns.
   */
  onDirectChange?: (field: keyof DirectFields, value: string) => void;
  /** P10 #22: picking a cms.locations row fills the deliver-to address +
   *  binds delivertocode. When present (with onDirectChange) the Delivery
   *  Location field becomes a typeahead; absent → plain free-text input. */
  onDirectPickLocation?: (row: LocationLookupRow) => void;
  /** Charge (tariff-rate) CRUD pass-throughs for TariffRatesCard. */
  chargesEditable?: boolean;
  /** Secured-quote add-only mode: Add is allowed and draft rows stay editable,
   *  but pre-existing (saved) charge rows are locked. Forwarded to the card. */
  chargesAddOnly?: boolean;
  chargeCatalog?: ChargeCatalogRow[];
  onAddCharge?: (p: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => void;
  onChangeCharge?: (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => void;
  onDeleteCharge?: (id: number) => void;
  /** Stopoffs pinned to the directquote row + CRUD pass-throughs. */
  stopoffs?: StopoffRow[];
  onAddStopoff?: (p: StopoffFieldsPayload) => void;
  onUpdateStopoff?: (stopId: number, p: StopoffFieldsPayload) => void;
  onDeleteStopoff?: (stopId: number) => void;
  /** Whether this quote has a saved ID. When false, attempting to add a stopoff
   *  shows a "save first" notice (stopoffs need a saved quote ID). Default true. */
  quoteSaved?: boolean;
  /**
   * Live rate-zone options (cms.ratezones): `value` is the ratezone CODE (what's
   * stored), `label` is the displayname (what V1 shows — select_html5.pl renders
   * `<option value=code>displayname</option>`). Falls back to the static
   * RATE_ZONES codes when the host doesn't supply the live list.
   */
  rateZoneOptions?: SelectOption[];
  /** The quote's bill-to recno — enables the FSC "Cust Tariff #" Lookup. */
  billTo?: number;
  /**
   * Secured-quote exemption: assign a still-MISSING customer FSC tariff even
   * though the tab is otherwise read-only (no `onDirectChange`). When provided,
   * the FSC "Cust Tariff #" Lookup stays available and picking a tariff routes
   * here instead of `onDirectChange`. Hosts pass this only when the SAVED quote
   * is secured + missing-tariff; once a tariff exists it re-locks. Mirrors the
   * secured add-only-charges deviation.
   */
  onAssignTariff?: (t: FscTariff) => void;
  /**
   * The SAVED customer FSC tariff number when that tariff has been DELETED from
   * fueldata.fsc_tariff (backend `fscTariffDeleted`). The row renders it struck
   * through in red with a "Deleted" tag, and — when a pick is allowed — the
   * number itself opens the Lookup. Passing the NUMBER (not a bare flag) keeps
   * the mark tied to the saved tariff: as soon as the draft points somewhere
   * else the replacement renders normally, without waiting for a reload.
   */
  deletedTariffNumber?: number | null;
  /**
   * Lock exemption: keep the Comments box editable even though the tab is
   * otherwise read-only (no `onDirectChange`) — comments are notes, not
   * pricing terms, so a secured/ended quote still accepts them (the backend
   * exempts directquote.comments from both locks). When `onDirectChange` is
   * present it wins; this only matters on a locked quote.
   */
  onCommentsChange?: (value: string) => void;
  /** Drawer-only per-quote Fuel Calculation / Rate Applies (masterquote fields,
   *  via HEADER_MAP). When `fuelCalc` is provided they render in column 3 under
   *  the FSC block, editable via onFuelRateChange. The standalone quote page
   *  omits them — they live in its Customer Info tab — so column 3 there is just
   *  Drayage + FSC. */
  fuelCalc?: string;
  rateApplies?: string;
  onFuelRateChange?: (field: 'fuelCalc' | 'rateApplies', value: string) => void;
  /**
   * When true, the lane-defining fields — Rate Zone, Delivery Type, and the
   * entire Delivery Location (name + address + city/state/zip) — render
   * read-only even though the rest of the tab stays editable. Hosts set this
   * for revision 2+ quotes: a revision must keep the original lane, so to
   * change the rate zone or delivery location the user duplicates the quote.
   * The first revision (unsecured) leaves these editable.
   */
  moveFieldsLocked?: boolean;
  /**
   * Secured-but-never-used exemption. A SECURED quote renders this tab read-only
   * (no `onDirectChange`), but while no order has used the quote its Move Type
   * may still be corrected. Hosts pass this (see `isMoveTypeEditable`) to keep
   * the Move Type select live on an otherwise locked tab; a short hint explains
   * why. Ignored when `onDirectChange` is present (Move Type is then an
   * ordinary editable field).
   */
  onMoveTypeChange?: (value: string) => void;
}

/**
 * Direct tab — single tab that DIRECT quotes show in place of WAREHOUSE's
 * Dray + Warehouse tabs. Reads directquote columns: equipment + cargo +
 * delivery destination + drayage charges. Mirrors the DirectTab section of
 * the claude.ai/design view-quote.jsx mockup.
 */
const DirectTab: React.FC<Props> = ({
  data, tariffRates = [], onDirectChange, onDirectPickLocation,
  chargesEditable, chargesAddOnly, chargeCatalog, onAddCharge, onChangeCharge, onDeleteCharge,
  stopoffs = [], onAddStopoff, onUpdateStopoff, onDeleteStopoff,
  quoteSaved = true,
  rateZoneOptions, billTo, onAssignTariff, deletedTariffNumber, onCommentsChange,
  fuelCalc, rateApplies, onFuelRateChange,
  moveFieldsLocked, onMoveTypeChange,
}) => {
  const dedupOpts = (current: string, rest: string[]) =>
    [current, ...rest].filter((v, i, a) => v && a.indexOf(v) === i);

  // Rate Zone options: `value` = ratezone CODE (what's stored), `label` =
  // displayname (what V1 shows). The host supplies the live cms.ratezones list;
  // absent that, fall back to the static codes (value = label). The current
  // stored code is always represented so an existing pick still renders even if
  // its zone was since removed from the table.
  const rateZoneSelectOptions = React.useMemo<SelectOption[]>(() => {
    const base: SelectOption[] = rateZoneOptions && rateZoneOptions.length
      ? rateZoneOptions
      : RATE_ZONES.map((z) => ({ value: z, label: z }));
    const cur = data.rateZone ?? '';
    const known = base.some((o) => (typeof o === 'string' ? o : o.value) === cur);
    return cur && !known ? [{ value: cur, label: cur }, ...base] : base;
  }, [rateZoneOptions, data.rateZone]);

  // Build an onChange handler for a field only when editing is enabled; when
  // `onDirectChange` is absent these resolve to `undefined`, so each input
  // falls back to its read-only display path (no behaviour change).
  const ch = (field: keyof DirectFields) =>
    onDirectChange ? (v: string) => onDirectChange(field, v) : undefined;

  // Lbs ↔ Kgs auto-calc (V1 new-quote parity): typing either weight derives
  // the other. A not-yet-numeric keystroke stages alone without cascading.
  const chWeight = (source: QuoteWeightField) =>
    onDirectChange
      ? (v: string) => {
          const patch = quoteWeightCascade(source, v);
          if (!patch) { onDirectChange(source, v); return; }
          onDirectChange('lbs', patch.lbs);
          onDirectChange('kgs', patch.kgs);
        }
      : undefined;

  // When not editable (no onDirectChange — e.g. a SECURED quote locked to End
  // Date only), the whole tab renders inside a disabled <fieldset>. Unlike the
  // header/customer tabs (which swap to <FieldValue>), this tab's inputs would
  // otherwise stay visually live but silently drop edits; `disabled` greys them
  // and blocks interaction. `display:contents` keeps each host's own layout/gap.
  const readOnly = !onDirectChange;

  // Move Type: an ordinary field when the tab is editable; on a read-only
  // (secured) tab it stays live ONLY via the host's `onMoveTypeChange` — the
  // never-used-on-an-order exemption. `moveTypeOnlyEditable` drives the hint.
  const moveTypeChange = ch('moveType') ?? onMoveTypeChange;
  const moveTypeOnlyEditable = readOnly && !!onMoveTypeChange;

  // N18: once a deliver-to location is bound (delivertocode > 0), its
  // denormalized address fields are frozen so they stay consistent with the
  // cms.locations master (formatview_quote.pl:604-635). The name stays
  // editable — re-picking via the lookup re-binds the code (P10).
  const deliverToFrozen = (data.deliverToCode ?? 0) > 0;

  // Revision 2+: the lane-defining fields (Rate Zone, Delivery Type, and the
  // whole Delivery Location) are frozen so a revision can't drift from the
  // original lane — to change them the user duplicates the quote. This locks
  // INDEPENDENTLY of the overall read-only state; the rest of the tab (drayage,
  // FSC, flags, charges, …) stays editable on an unsecured revision.
  //
  // …but only once the lane is actually COMPLETE (a rate zone, a delivery type
  // and a destination). A revision that came over without its lane — quote
  // 113635-2, created with "Rates / equipment" unchecked back when that blanked
  // the whole directquote row — would otherwise be locked into being wrong.
  // Staying unlocked until the lane is whole also means a partial repair can be
  // saved without locking the CSR out of finishing it.
  const laneComplete = !!(
    (data.rateZone ?? '') && (data.deliveryZone ?? '')
    && ((data.deliverToZip ?? '') || ((data.deliverToCity ?? '') && (data.deliverToState ?? '')))
  );
  const moveLocked = !!moveFieldsLocked && laneComplete;
  // Address fields freeze when bound to a location (N18) OR move-locked.
  const addrFrozen = deliverToFrozen || moveLocked;

  // A locked-down field shows its value (not an input) so it's clear it can't be
  // edited. The whole tab is read-only when there's no onDirectChange.
  const roValue = (value: string, width?: number) => <FieldValue value={value || '—'} width={width} />;

  // Drayage Rate shows the cents the quote actually stores. It used to round to whole
  // dollars "for display", which put 321 here while Total w/Charges below it read 321.10
  // — the same stored number, disagreeing with itself on one panel — and the editable
  // input has always shown the exact value, so read-only and edit mode disagreed too.
  // Blank / non-numeric passes through so the "—" placeholder still shows.

  // "Delivery rate is not defined …" modal (LOCAL standards zone, no pairing).
  const [drayMsg, setDrayMsg] = React.useState<string | null>(null);
  // FSC "Cust Tariff #" Lookup picker.
  const [tariffLookupOpen, setTariffLookupOpen] = React.useState(false);
  // "Show Miles" round-trip breakdown modal (V1 getmiles_Google).
  const [showMilesOpen, setShowMilesOpen] = React.useState(false);
  const canShowMiles = !!(
    (data.rateZone ?? '') &&
    ((data.deliverToZip ?? '') || ((data.deliverToCity ?? '') && (data.deliverToState ?? '')))
  );

  // Zip↔city/state autofill + rate-zone mileage (V1 getcitystatezip/calcmiles).
  // Only when editable; address autofill is skipped while location-linked.
  const geo = useQuoteDeliveryGeo({
    ratezone: data.rateZone ?? '',
    deliveryzone: data.deliveryZone ?? '',
    zip: data.deliverToZip ?? '',
    city: data.deliverToCity ?? '',
    state: data.deliverToState ?? '',
    enabled: !!onDirectChange,
    lockAddress: addrFrozen,
    onCityState: (city, state) => { onDirectChange?.('deliverToCity', city); onDirectChange?.('deliverToState', state); },
    onZip: (zip) => onDirectChange?.('deliverToZip', zip),
    onMiles: (m) => onDirectChange?.('deliverToMiles', m),
    miles: data.deliverToMiles ?? '',
    onDrayRate: (r) => onDirectChange?.('drayRate', r),
    // LOCAL standards zone with no (zone,zip) pairing → modal + clear the rate (V1).
    onNotDefined: (msg) => { setDrayMsg(msg); onDirectChange?.('drayRate', ''); },
  });

  // Total With Charges = dray rate + Σ IN RATE additional charges, recomputed
  // live (V1 _recompute_directquote_total) so editing the Drayage updates the
  // total AND the FSC base immediately — matching the new-quote form. 'EXTRA IN
  // RATE' charges do NOT roll in.
  const inRateCharges = tariffRates.reduce(
    (sum, r) => (r.rateType === 'IN RATE' ? sum + (Number(r.rate) || 0) : sum), 0);
  const totalWithCharges = ((Number(data.drayRate) || 0) + inRateCharges).toFixed(2);

  // Mirror the recomputed total back into the draft so the header's estimated
  // revenue + the save payload use the live figure too. The first run (load) is
  // skipped so a rounding-equal value never reads as a spurious unsaved change.
  const totalSynced = React.useRef(false);
  React.useEffect(() => {
    if (!onDirectChange) return;
    if (!totalSynced.current) { totalSynced.current = true; return; }
    if (totalWithCharges !== (data.drayWithCharges ?? '')) onDirectChange('drayWithCharges', totalWithCharges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalWithCharges]);

  // FSC: rate auto-fills + locks for CURRENT/CUSTOMER, editable for
  // PCT%/MILES/DISCOUNTED; `adjustment` feeds the estimated revenue.
  const fsc = useQuoteFsc({
    fsctype: data.fscType ?? '',
    fsctariff: data.fscTariff != null ? String(data.fscTariff) : '',
    deliveryzone: data.deliveryZone ?? '',
    fscrate: data.fscRate ?? '',
    dray: totalWithCharges,
    miles: data.deliverToMiles ?? '',
    enabled: !!onDirectChange,
    // Read-only view: still resolve the CUSTOMER tariff ratetype so the FSC unit
    // renders "Per Mile" (not "%") for a saved per-mile quote.
    resolveReadOnly: true,
    onFscRate: (r) => onDirectChange?.('fscRate', r),
  });
  // Unit shown after the FSC rate (% of dray vs $ per mile), per type.
  const fscUnit = fscRateUnit(data.fscType ?? '', fsc.ratetype);
  // Both auto-filled types are dynamic, so show the live rate resolved from
  // /quotes/fsc rather than the stored snapshot:
  //  • CURRENT (V2 GWI = "GWI's current published fuel %") — 0 on a migrated quote
  //    until re-saved;
  //  • CUSTOMER — the referenced tariff's rate drifts (a WEEKLY/MONTHLY line moves
  //    each week, a FUEL INDEX rate tracks the fuel index), so a stored snapshot
  //    goes stale (e.g. quote showed 45% while tariff 1039's current rate is 49.5%).
  // The actual invoice FSC re-resolves from the tariff at pickup date, so the stored
  // value isn't authoritative. Fall back to the stored rate when nothing resolves
  // (no tariff / missing tariff / resolve failed). Other types keep their stored rate.
  const fscTypeUpper = (data.fscType ?? '').trim().toUpperCase();
  const showsResolvedFsc = fscTypeUpper === 'CURRENT' || fscTypeUpper === 'CUSTOMER';
  // A CUSTOMER lane with no tariff can't resolve an FSC: the invoice re-resolves
  // from the (absent) tariff at pickup, so $0 is actually charged. Don't display
  // the stored migration snapshot (e.g. 113958's 31%) beside the "Missing Tariff"
  // badge — that pairing is contradictory. Show blank until a tariff is assigned.
  const missingCustTariff = isMissingCustomerTariff(data.fscType, data.fscTariff);
  const displayFscRate = missingCustTariff
    ? ''
    : showsResolvedFsc && fsc.resolvedRate != null ? fsc.resolvedRate : data.fscRate;

  // N20: lbs/kgs are highlighted against the quote-specific overweight matrix.
  const ow = quoteOverweight(data, 'DIRECT');

  // N42: dray rate-per-mile (drayrate / delivertomiles), mirroring DeliveryTab.
  const ratePerMile = React.useMemo(() => {
    const miles = parseFloat(data.deliverToMiles);
    const rate = parseFloat(data.drayRate);
    if (!miles || !rate) return '';
    return `${(rate / miles).toFixed(2)} / mile`;
  }, [data.deliverToMiles, data.drayRate]);

  // DEBUG: which dray-rate source/ratetype the standard came from ('Flat' /
  // 'Per Mile'), shown far-right on the Rate line. Blank for deliverystandards.
  const drayRateTypeLabel = (() => {
    const rt = (geo.drayInfo?.ratetype || '').trim().toUpperCase();
    return rt === 'FLAT' ? 'Flat' : rt === 'PER MILE' ? 'Per Mile' : '';
  })();

  // Combined "City, State Zip" for the locked (read-only) delivery address.
  const cityStateZip = [
    [data.deliverToCity, data.deliverToState].map((s) => (s ?? '').trim()).filter(Boolean).join(', '),
    (data.deliverToZip ?? '').trim(),
  ].filter(Boolean).join(' ');

  // Cust Tariff # renders as a link into the native Fuel Surcharge Tariff
  // maintenance page (new tab, pre-filtered to this tariff number). Null when
  // there's no tariff yet — callers fall back to a plain "—".
  const tariffNum = Number(data.fscTariff) || 0;
  const tariffLink = tariffNum > 0 ? (
    <a
      href={`/data-management/fuel-surcharge-tariff?q=${tariffNum}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Open this tariff in Fuel Surcharge Tariff maintenance"
      // px-1.5 matches FieldValue / the inputs so the tariff # lines up with the
      // FSC value and the Fuel Type control above it.
      className="px-1.5 text-[0.78125rem] font-semibold text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
    >
      {tariffNum}
    </a>
  ) : null;

  // The saved tariff has been deleted from fueldata (every revision deleted='Y'),
  // so the link is dead — nothing resolves and the invoice charges no FSC. Only
  // marks the SAVED number: a replacement picked in this session renders normally.
  const tariffDeleted = tariffNum > 0 && tariffNum === (Number(deletedTariffNumber) || 0);
  // Whether this host lets the tariff be re-picked at all (an editable tab, or a
  // secured quote exempted to fix a broken link) — and there's a bill-to to look
  // tariffs up for. Drives whether the struck-through number is clickable.
  const tariffPickable = !!(onDirectChange || onAssignTariff) && (billTo ?? 0) > 0;
  const deletedTariffStyle = 'px-1.5 text-[0.78125rem] font-semibold line-through text-red-600 dark:text-red-400';
  const deletedTariffTitle = tariffPickable
    ? 'This fuel tariff has been deleted — click to pick a new one'
    : 'This fuel tariff has been deleted';
  const deletedTariffMark = (
    <>
      {tariffPickable ? (
        <button
          type="button"
          onClick={() => setTariffLookupOpen(true)}
          title={deletedTariffTitle}
          className={`${deletedTariffStyle} hover:text-red-700 dark:hover:text-red-300`}
        >
          {tariffNum}
        </button>
      ) : (
        <span title={deletedTariffTitle} className={deletedTariffStyle}>{tariffNum}</span>
      )}
      <span className="text-[0.71875rem] font-semibold text-red-600 dark:text-red-400">
        {DELETED_TARIFF_LABEL}
      </span>
    </>
  );

  // CUSTOMER FSC with no tariff number (e.g. a migrated quote whose customer
  // tariff isn't in v1 yet — the migration no longer auto-creates it). Shown in
  // place of the tariff link so the gap is obvious and a CSR can assign one.
  const missingTariffBadge = (
    <span
      title="This customer's fuel-surcharge tariff isn't set up yet"
      className="px-1.5 text-[0.78125rem] font-semibold text-red-600 dark:text-red-400"
    >
      {MISSING_TARIFF_LABEL}
    </span>
  );

  // N13: a charge's modifiers render in the line item's "Additional" column, so
  // each appears only when that charge is on the quote. The chassis-fee Type
  // (FLAT/PER DAY/MIN/MAX — V1 chassisfeetype_data) shows a Days input ONLY for
  // MIN/MAX (FLAT/PER DAY need no day count); WAITING TIME / YARD STORAGE show
  // their free-time value. Inputs are read-only when onDirectChange is absent
  // (ch() → no handler), so a read-only quote still shows the values.
  const modLbl = 'text-[0.6875rem] text-slate-500 dark:text-slate-400 flex-shrink-0';
  const chassisAdditional = (row: TariffRateRow, legacyType: string | undefined, legacyDays: number | null | undefined,
                             typeField: keyof DirectFields, daysField: keyof DirectFields,
                             ariaPrefix: string): React.ReactNode => {
    const type = row.additionalType ?? legacyType;
    const days = row.additionalType != null ? row.additionalDays : legacyDays;
    const showDays = !chassisDaysReadOnly(type); // MIN / MAX carry a day count; FLAT / PER DAY don't.
    return (
      <span className="flex items-center gap-1.5 min-w-0">
        <SelectInput value={type ?? ''} onChange={onDirectChange ? (v) => {
          onDirectChange(typeField, v);
          onChangeCharge?.(row.id, { additionaltype: v, additionaldays: ['MIN', 'MAX'].includes(v) ? Number(days) || 0 : 0 });
          if (!['MIN', 'MAX'].includes(v)) onDirectChange(daysField, '0');
        } : undefined} options={dedupOpts(type ?? '', ['', ...CHASSIS_FEE_TYPES])} placeholder="—" ariaLabel={`${ariaPrefix} Type`} />
        {showDays && (
          <>
            <TextInput value={days != null ? String(days) : ''} onChange={onDirectChange ? (v) => { onDirectChange(daysField, v); onChangeCharge?.(row.id, { additionaltype: type || '', additionaldays: Number(v) || 0 }); } : undefined} ariaLabel={`${ariaPrefix} Days`} width={44} />
            <span className={modLbl}>Days</span>
          </>
        )}
      </span>
    );
  };
  const rowModifier = (row: TariffRateRow): React.ReactNode => {
    const desc = (row.description || '').toUpperCase();
    // Check LIGHTWEIGHT first so a lightweight variant can't fall through to
    // the standard chassis-fee controls.
    if (desc.includes('LIGHTWEIGHT CHASSIS')) {
      return chassisAdditional(row, data.lightweightChassisFeeType, data.lightweightChassisFeeDays, 'lightweightChassisFeeType', 'lightweightChassisFeeDays', 'Lightweight Chassis Fee');
    }
    if (desc.includes('CHASSIS FEE')) {
      return chassisAdditional(row, data.chassisFeeType, data.chassisFeeDays, 'chassisFeeType', 'chassisFeeDays', 'Chassis Fee');
    }
    // WAITING TIME / YARD STORAGE free time now lives in the Free Time column
    // (which mirrors it to the V1 directquote field — see onFreeTimeChange).
    return null;
  };

  // Free Time column value/unit + writer. For WAITING TIME / YARD STORAGE the
  // shown value mirrors the V1 directquote field (waitingtimefree hours /
  // yardstoragefree days); editing writes BOTH that field AND the charge's own
  // quoteadditionalcharges.freetime so V1 and FastCMS stay in sync.
  //
  // "GROUNDED YARD STORAGE" is a SEPARATE charge that is NOT backed by a V1
  // directquote field — it keeps its own quoteadditionalcharges.freetime. It
  // contains the substring "YARD STORAGE", so it must be excluded here or its
  // free days get mirrored to the single shared yardStorageFree field (both
  // rows then read/write the same value — Quote #113877 bug).
  const isYardStorage = (desc: string) => desc.includes('YARD STORAGE') && !desc.includes('GROUNDED');
  const freeTimeFor = (row: TariffRateRow): { value: string; unit: string } => {
    const desc = (row.description || '').toUpperCase();
    if (desc.includes('WAITING TIME')) return { value: data.waitingTimeFree ?? '', unit: row.freeTimeUnit || 'HOURS' };
    if (isYardStorage(desc)) return { value: data.yardStorageFree != null ? String(data.yardStorageFree) : '', unit: row.freeTimeUnit || 'DAYS' };
    return { value: row.freeTime ?? '', unit: row.freeTimeUnit || 'DAYS' };
  };
  const onFreeTimeChange = onChangeCharge
    ? (row: TariffRateRow, value: number, unit: string) => {
        onChangeCharge(row.id, { freetime: value, freetimeunit: unit });
        const desc = (row.description || '').toUpperCase();
        if (desc.includes('WAITING TIME')) onDirectChange?.('waitingTimeFree', String(value));
        else if (isYardStorage(desc)) onDirectChange?.('yardStorageFree', String(value));
      }
    : undefined;
  // When a charge is added via the popup, persist its Additional/Free-Time extras
  // to the V1 directquote fields (the charge's own freetime is set by the add).
  const onAddModifier = onDirectChange
    ? (description: string, extras: { freetime?: number; chassisType?: string; chassisDays?: number }) => {
        const desc = description.toUpperCase();
        if (desc.includes('WAITING TIME')) {
          if ((extras.freetime ?? 0) > 0) onDirectChange('waitingTimeFree', String(extras.freetime));
        } else if (isYardStorage(desc)) {
          if ((extras.freetime ?? 0) > 0) onDirectChange('yardStorageFree', String(extras.freetime));
        } else if (desc.includes('LIGHTWEIGHT CHASSIS')) {
          if (extras.chassisType !== undefined) { onDirectChange('lightweightChassisFeeType', extras.chassisType); onDirectChange('lightweightChassisFeeDays', String(extras.chassisDays ?? 0)); }
        } else if (desc.includes('CHASSIS FEE')) {
          if (extras.chassisType !== undefined) { onDirectChange('chassisFeeType', extras.chassisType); onDirectChange('chassisFeeDays', String(extras.chassisDays ?? 0)); }
        }
      }
    : undefined;

  return (
    <>
    {/* No `disabled` on the fieldset: when read-only, every field already
        renders as a value (not an input), and disabling the fieldset would also
        kill read-only-safe actions like "Show Miles". */}
    <fieldset className="contents">
      <div className="dt-cards">
        <div className="dt-cards-grid">
        {/* Column 1 — Move Details (equipment + cargo) + Flags + Comments. */}
        <div className="dt-col">
        <Card compact collapsible={false} scaleKey="moveDetails" icon={<TruckIcon className="h-3.5 w-3.5" />} title="Move Details">
          <Row>
            <Label width={LBL}>Move Type</Label>
            {moveTypeChange
              ? <SelectInput value={data.moveType} onChange={moveTypeChange} options={dedupOpts(data.moveType, ['', ...MOVE_TYPES])} placeholder="—" ariaLabel="Move Type" />
              : roValue(data.moveType)}
          </Row>
          {moveTypeOnlyEditable && (
            <div className="text-[0.65625rem] text-slate-400 dark:text-slate-500 mb-1.5">
              Move Type stays editable until this quote is used on an order.
            </div>
          )}
          <Row>
            <Label width={LBL}>Size / Type</Label>
            {readOnly ? roValue([data.size, data.equipType].filter(Boolean).join(' / ')) : (
              <>
                {/* Empty option + "—" placeholder so a blank value shows "—" (not a
                    misleading first option like "20") and must be actively picked —
                    matching Move Type above. */}
                <SelectInput value={data.size} onChange={ch('size')} options={dedupOpts(data.size, ['', ...SIZES])} placeholder="—" width={64} ariaLabel="Size" />
                <SelectInput value={data.equipType} onChange={ch('equipType')} options={dedupOpts(data.equipType, ['', ...EQUIP_TYPES])} placeholder="—" ariaLabel="Equipment Type" />
              </>
            )}
          </Row>
          <Row><Label width={LBL}>Commodity</Label>{readOnly ? roValue(data.commodity) : <TextInput value={data.commodity} onChange={ch('commodity')} placeholder="—" />}</Row>
          <Row>
            <Label width={LBL}>Lbs / Kgs</Label>
            {readOnly ? roValue(`${data.lbs || '0'} / ${data.kgs || '0'}${data.legalWeight === 'Y' ? '  (Legal)' : ''}`) : (
              <>
                <TextInput value={data.lbs} onChange={chWeight('lbs')} highlight={ow.lbsOver ? 'red' : undefined} ariaLabel="Lbs" width={96} placeholder="—" />
                <TextInput value={data.kgs} onChange={chWeight('kgs')} highlight={ow.kgsOver ? 'red' : undefined} ariaLabel="Kgs" width={96} placeholder="—" />
                <label className="inline-flex items-center gap-1 text-[0.71875rem] text-slate-600 dark:text-slate-300 cursor-pointer ml-1">
                  <input
                    type="checkbox"
                    checked={data.legalWeight === 'Y'}
                    onChange={(e) => onDirectChange('legalWeight', e.target.checked ? 'Y' : 'N')}
                  /> Legal
                </label>
              </>
            )}
          </Row>

          <Divider />

          <div className="text-[0.625rem] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">Flags</div>
          <div className="grid grid-cols-2 gap-x-1.5">
            <FlagInput label="Overweight" value={data.overweight as FlagValue} onChange={ch('overweight')} labelWidth={80} readOnly={readOnly} />
            <FlagInput label="OOG"        value={data.oog as FlagValue}        onChange={ch('oog')}        labelWidth={80} readOnly={readOnly} />
            <FlagInput label="Triaxle"    value={data.triaxle as FlagValue}    onChange={ch('triaxle')}    labelWidth={80} readOnly={readOnly || triaxleReadOnly(data, true)} />
            <FlagInput label="Swing"      value={data.swing as FlagValue}      onChange={ch('swing')}      labelWidth={80} readOnly={readOnly || swingReadOnly(data)} />
            <FlagInput label="Hazmat"     value={data.hazmat as FlagValue}     onChange={ch('hazmat')}     labelWidth={80} readOnly={readOnly} />
            <div />
          </div>
        </Card>

        <Card compact collapsible={false} scaleKey="directComments" icon={<ChatBubbleLeftIcon className="h-3.5 w-3.5" />} title="Comments">
          {(onDirectChange || onCommentsChange) ? (
            <textarea
              value={data.comments}
              onChange={(e) => (onDirectChange ? onDirectChange('comments', e.target.value) : onCommentsChange!(e.target.value))}
              placeholder="Notes for this lane…"
              className="w-full min-h-[100px] border border-slate-300 rounded px-2 py-1.5 text-[0.75rem] text-slate-900 resize-y bg-white font-sans dark:border-slate-600 dark:text-slate-100 dark:bg-slate-900"
            />
          ) : (
            <div className="w-full min-h-[100px] text-[0.75rem] text-slate-700 whitespace-pre-wrap dark:text-slate-200">
              {data.comments || <span className="text-slate-400 dark:text-slate-500">No comments</span>}
            </div>
          )}
        </Card>
        </div>

        {/* Column 2 — Rate/Delivery zones, Delivery Location, Stopoffs. */}
        <div className="dt-col">
        <Card compact collapsible={false} scaleKey="rateDeliveryZone" icon={<Icon.map className="h-3.5 w-3.5" />} title="Rate / Delivery Zone">
          {moveLocked && <LockHint />}
          <Row>
            <Label width={LBL}>Rate Zone</Label>
            {moveLocked || readOnly
              ? <FieldValue value={data.rateZone || '—'} />
              : <SelectInput value={data.rateZone} onChange={ch('rateZone')} options={rateZoneSelectOptions} placeholder="—" ariaLabel="Rate Zone" />}
          </Row>
          <Row>
            <Label width={LBL}>Delivery Type</Label>
            {moveLocked || readOnly
              ? <FieldValue value={data.deliveryZone || '—'} />
              /* Delivery Type is required to save, so a blank flags itself red
                 rather than only failing at Save. It also decides how the dupe
                 check matches (LOCAL on zip, else city+state), so a blank is a
                 real defect, not just an empty field. */
              : <SelectInput value={data.deliveryZone} onChange={ch('deliveryZone')} options={dedupOpts(data.deliveryZone, ['', ...DELIVERY_ZONES])} placeholder="—" ariaLabel="Delivery Type" highlight={(data.deliveryZone ?? '').trim() ? undefined : 'red'} />}
          </Row>
        </Card>

        <Card compact collapsible={false} scaleKey="deliveryLocation" icon={<Icon.location className="h-3.5 w-3.5" />} title="Delivery Location">
          {moveLocked && <LockHint />}
          {moveLocked || readOnly ? (
            // Locked (revision or read-only quote): read-only values, and only
            // the rows that actually carry data (e.g. a city/state/zip-only
            // address skips the empty name + address lines).
            <>
              {data.deliverToName && (
                <Row><Label width={LBL}>Delivery Location</Label><FieldValue value={data.deliverToName} /></Row>
              )}
              {data.deliverToAddr1 && (
                <Row><Label width={LBL}>Address 1</Label><FieldValue value={data.deliverToAddr1} /></Row>
              )}
              {data.deliverToAddr2 && (
                <Row><Label width={LBL}>Address 2</Label><FieldValue value={data.deliverToAddr2} /></Row>
              )}
              {cityStateZip && (
                <Row><Label width={LBL}>City / State / Zip</Label><FieldValue value={cityStateZip} /></Row>
              )}
            </>
          ) : (
            <>
              <Row>
                <Label width={LBL}>Delivery Location</Label>
                {onDirectPickLocation ? (
                  <LocationTypeAhead
                    value={data.deliverToName}
                    onType={(v) => { onDirectChange('deliverToName', v); onDirectChange('deliverToCode', '0'); }}
                    onPick={onDirectPickLocation}
                    ariaLabel="Delivery Location"
                    placeholder="Search locations…"
                    className="flex-1 min-w-0 max-w-[300px]"
                  />
                ) : (
                  <TextInput value={data.deliverToName} onChange={ch('deliverToName')} ariaLabel="Delivery Location" placeholder="—" className="!max-w-[300px]" />
                )}
              </Row>
              <Row><Label width={LBL}>Address 1</Label>{addrFrozen ? roValue(data.deliverToAddr1) : <TextInput value={data.deliverToAddr1} onChange={ch('deliverToAddr1')} ariaLabel="Deliver-To Address 1" placeholder="—" className="!max-w-[300px]" />}</Row>
              <Row><Label width={LBL}>Address 2</Label>{addrFrozen ? roValue(data.deliverToAddr2) : <TextInput value={data.deliverToAddr2} onChange={ch('deliverToAddr2')} ariaLabel="Deliver-To Address 2" placeholder="—" className="!max-w-[300px]" />}</Row>
              <Row>
                <Label width={LBL}>City / State / Zip</Label>
                {addrFrozen ? <FieldValue value={cityStateZip || '—'} /> : (
                  <>
                    <TextInput value={data.deliverToCity} onChange={ch('deliverToCity')} onBlur={geo.handleCityStateBlur} ariaLabel="Deliver-To City" />
                    <TextInput value={data.deliverToState} onChange={ch('deliverToState')} onBlur={geo.handleCityStateBlur} ariaLabel="Deliver-To State" width={48} />
                    <TextInput value={data.deliverToZip} onChange={ch('deliverToZip')} onBlur={geo.handleZipBlur} ariaLabel="Deliver-To Zip" width={96} />
                  </>
                )}
              </Row>
            </>
          )}
        </Card>

        <StopoffsEditor
          stopoffs={stopoffs}
          emptyLabel="No stopoffs on this quote"
          quoteSaved={quoteSaved}
          onAdd={onAddStopoff}
          onUpdate={onUpdateStopoff}
          onDelete={onDeleteStopoff}
        />
        </div>

        {/* Column 3 — Drayage & Fuel (+ Fuel Calc / Rate Applies on the drawer). */}
        <div className="dt-col">
        <Card compact collapsible={false} scaleKey="drayageFuel" icon={<TruckIcon className="h-3.5 w-3.5" />} title="Drayage & Fuel">
          <Row>
            <Label width={LBL}>Miles</Label>
            {/* Miles is always system-computed (rate-zone origin → delivery) — never
                hand-edited, so it shows as a value, not an input. */}
            <FieldValue value={data.deliverToMiles || '—'} width={96} />
            {canShowMiles && (
              <button
                type="button"
                onClick={() => setShowMilesOpen(true)}
                className="text-[0.71875rem] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Show Miles
              </button>
            )}
            {geo.geoBusy && <span className="text-[0.6875rem] text-slate-400 dark:text-slate-500">Calculating…</span>}
          </Row>
          {geo.milesError && (
            <div className="text-[0.6875rem] text-amber-600 dark:text-amber-400" style={{ paddingLeft: LBL }}>{geo.milesError}</div>
          )}
          <Row>
            <Label width={LBL}>Rate</Label>
            {readOnly ? roValue(formatQuoteRate(data.drayRate), 120) : <TextInput value={data.drayRate} onChange={ch('drayRate')} ariaLabel="Rate" width={120} placeholder="—" />}
            {/* Live per-mile the typed rate works out to — stays next to the input. */}
            {ratePerMile && <span className="text-[0.6875rem] text-slate-400 dark:text-slate-500">{ratePerMile}</span>}
            {/* DEBUG: dray-rate ratetype, far right in light grey. */}
            {drayRateTypeLabel && (
              <span className="ml-auto text-[0.6875rem] text-slate-300 dark:text-slate-600">({drayRateTypeLabel})</span>
            )}
          </Row>
          {/* Standard reference sits UNDER the Rate input; indent so the text
              lines up with the input's LEFT edge (label width + Row gap-2). */}
          <div style={{ paddingLeft: LBL + 8 }}>
            <DrayRateHint info={geo.drayInfo} drayRate={Number(data.drayRate) || 0} miles={Number(data.deliverToMiles) || 0} />
          </div>
          <AutoCalcRow label="Total w/Charges" value={totalWithCharges} labelWidth={LBL} />

          <Divider />

          {/* FSC: Fuel Type, then (CUSTOMER only) the Cust Tariff # on its own
              row, then the calculated FSC rate. */}
          <Row>
            <Label width={LBL}>Fuel Type</Label>
            {/* Switching the type clears the rate; the hook re-fills CURRENT/CUSTOMER. */}
            {readOnly
              ? roValue(data.fscType, 130)
              /* Fuel Type is required to save (a blank fsctype means the invoice
                 never charges FSC), so a blank flags itself red rather than only
                 failing at Save. The blank option stays so a legacy blank renders
                 instead of silently snapping to another value. */
              : <SelectInput value={data.fscType} onChange={(v) => { onDirectChange('fscType', v); onDirectChange('fscRate', ''); }} options={dedupOpts(data.fscType, ['', ...FSC_TYPES])} width={130} placeholder="—" ariaLabel="Fuel Type" highlight={(data.fscType ?? '').trim() ? undefined : 'red'} />}
          </Row>
          {data.fscType === 'CUSTOMER' && (
            <Row>
              <Label width={LBL}>Cust Tariff #</Label>
              {/* Lookup shows when the tab is editable (onDirectChange) OR when a
                  secured quote is exempted to assign a still-missing tariff
                  (onAssignTariff); otherwise the tariff is read-only. */}
              {(onDirectChange || onAssignTariff) ? (
                <>
                  {/* Tariff # is a link into the tariff maintenance page (or a
                      struck-through "Deleted" mark when it no longer exists);
                      replace it via Lookup. */}
                  {tariffDeleted ? deletedTariffMark : (tariffLink ?? missingTariffBadge)}
                  <button
                    type="button"
                    onClick={() => setTariffLookupOpen(true)}
                    disabled={!((billTo ?? 0) > 0)}
                    title={(billTo ?? 0) > 0 ? 'Look up a customer FSC tariff' : 'No bill-to on this quote'}
                    className="text-[0.71875rem] font-semibold text-blue-600 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-default dark:text-blue-400 dark:hover:text-blue-300 dark:disabled:text-slate-600"
                  >
                    Lookup
                  </button>
                </>
              ) : (
                tariffDeleted ? deletedTariffMark : (tariffLink ?? missingTariffBadge)
              )}
            </Row>
          )}
          <Row>
            <Label width={LBL}>FSC</Label>
            {/* Calculated rate (CURRENT / CUSTOMER) shows as text; only the
                user-entered types (PCT% / MILES / DISCOUNTED) get an input box. */}
            {fsc.fscReadOnly
              ? <FieldValue value={displayFscRate || '—'} width={96} />
              : <TextInput value={data.fscRate} onChange={ch('fscRate')} ariaLabel="FSC Rate" width={96} placeholder="—" />}
            {fscUnit && !missingCustTariff && <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400 w-14">{fscUnit}</span>}
          </Row>

          {/* Drawer only: per-quote Fuel Calculation / Rate Applies (stacked). The
              standalone quote page leaves these in its Customer Info tab. The
              drawer decides editability (they stay open on a secured/ended
              quote, as in V1), so `readOnly` is deliberately NOT consulted. */}
          {fuelCalc != null && (
            <>
              <Divider />
              {/* Both are required to save, so a blank flags itself red rather
                  than only failing at Save. SelectInput forces a blank row for a
                  value the option list doesn't carry, so a legacy blank shows as
                  blank instead of snapping to the first choice. */}
              <Row>
                <Label width={LBL}>Fuel Calculation</Label>
                {onFuelRateChange
                  ? <SelectInput value={fuelCalc} onChange={(v) => onFuelRateChange('fuelCalc', v)} options={withCurrent(fuelCalc, FUEL_CALC_TYPES)} ariaLabel="Fuel Calculation" highlight={fuelCalc.trim() ? undefined : 'red'} />
                  : <FieldValue value={fuelCalc} />}
              </Row>
              <Row>
                <Label width={LBL}>Rate Applies</Label>
                {onFuelRateChange
                  ? <SelectInput value={rateApplies ?? ''} onChange={(v) => onFuelRateChange('rateApplies', v)} options={withCurrent(rateApplies ?? '', RATE_APPLIES)} ariaLabel="Rate Applies" highlight={(rateApplies ?? '').trim() ? undefined : 'red'} />
                  : <FieldValue value={rateApplies ?? ''} />}
              </Row>
            </>
          )}
        </Card>
        </div>
        </div>
      </div>
    </fieldset>

    {/* Rendered OUTSIDE the disabled fieldset so the card's read-only
        affordances (column sorting, the Tariff Rates reference popup) keep
        working on a locked/secured quote. The card disables its own EDITING
        (the + Add New button + per-row controls) internally via `editable`. */}
    <TariffRatesCard
      rows={tariffRates}
      editable={chargesEditable}
      addOnly={chargesAddOnly}
      catalog={chargeCatalog}
      onAdd={onAddCharge}
      onChangeRow={onChangeCharge}
      onDeleteRow={onDeleteCharge}
      rowModifier={rowModifier}
      freeTimeFor={freeTimeFor}
      onFreeTimeChange={onFreeTimeChange}
      onAddModifier={onAddModifier}
    />

    <AlertModal open={!!drayMsg} title="Delivery rate" message={drayMsg} onClose={() => setDrayMsg(null)} />
    <ShowMilesModal
      open={showMilesOpen}
      ratezone={data.rateZone ?? ''}
      deliveryzone={data.deliveryZone ?? ''}
      zip={data.deliverToZip ?? ''}
      city={data.deliverToCity ?? ''}
      state={data.deliverToState ?? ''}
      onClose={() => setShowMilesOpen(false)}
    />
    <FscTariffLookupModal
      open={tariffLookupOpen}
      billto={billTo ?? 0}
      onClose={() => setTariffLookupOpen(false)}
      onPick={(t) => {
        // Editable tab → feed the three FSC fields through the normal change
        // callback. Secured missing-tariff exemption (no onDirectChange) → route
        // to onAssignTariff so the host stages just the tariff assignment.
        if (onDirectChange) {
          onDirectChange('fscType', 'CUSTOMER');
          onDirectChange('fscTariff', String(t.tariffNumber));
          onDirectChange('fscRate', t.currentRate != null ? String(t.currentRate) : '');
        } else {
          onAssignTariff?.(t);
        }
      }}
    />
    </>
  );
};

const Divider: React.FC = () => <div className="h-px bg-slate-100 dark:bg-slate-700 my-1" />;

// Shown on the Rate/Delivery Zone + Delivery Location cards of a revision 2+
// quote, where those fields are frozen (change them by Duplicating the quote).
const LockHint: React.FC = () => (
  <div className="flex items-center gap-1 text-[0.65625rem] text-slate-400 dark:text-slate-500 mb-1.5">
    <LockClosedIcon className="h-3 w-3 flex-shrink-0" />
    <span>Locked on revisions — <span className="font-semibold">Duplicate</span> to change.</span>
  </div>
);

export default DirectTab;
